import React, {Suspense, lazy} from 'react';
import PropTypes from 'prop-types';
import {
  Route, Switch, Redirect, withRouter,
} from 'react-router';
import {auth, notAuth, requireAuth} from './utils/utils-auth';
import {ROUTES, oneOf} from './configs/routes';
import RootLayout from './components/RootLayout/RootLayout';
import {USER_ROLE, USER_AUTHORITY_LEVEL} from './constants/user';
import CommonPageWrap from './components/CommonPageWrap/CommonPageWrap';
import URL_MAP from './constants/urls';

const {
  CANDIDATE, EMPLOYER, RECRUITER, ASSISTANT, ADMINISTRATOR,
} = USER_ROLE;
const {USER, ADMIN, SUPER_ADMIN} = USER_AUTHORITY_LEVEL;

const LoginPage = withRouter(lazy(() => import('./pages/LoginPage/LoginPage')));
const RegistrationPage = withRouter(lazy(() => import('./pages/RegistrationPages/RegistrationPage')));
const RestorePasswordPage = withRouter(lazy(() => import('./pages/RestorePasswordPage/RestorePasswordPage')));
const IndexPage = withRouter(lazy(() => import('./pages/LandingPage')));
const TariffPage = withRouter(lazy(() => import('./pages/TariffPage')));
const AboutCompany = withRouter(lazy(() => import('./pages/AboutCompany')));
const YouCandidatePage = withRouter(lazy(() => import('./pages/CandidatePage/CandidatePage')));
const RoleDetailsPage = withRouter(lazy(() => import('./pages/RoleDetailsPage')));
const YouEmployerPage = withRouter(lazy(() => import('./pages/EmployerPage/EmployerPage')));
const YouAgencyPage = withRouter(lazy(() => import('./pages/AgencyPage/AgencyPage')));
const NewsListPage = withRouter(lazy(() => import('./pages/NewsPage/NewsListPage')));
const NewsPage = withRouter(lazy(() => import('./pages/NewsPage/NewsPage')));
const ContactsPage = withRouter(lazy(() => import('./pages/ContactsPage/ContactsPage')));
const RequisitesPage = withRouter(lazy(() => import('./pages/RequisitesPage/RequisitesPage')));
const TechnicalSupport = withRouter(lazy(() => import('./pages/TechnicalSupport/TechnicalSupport')));
const SoftwareRequirements = withRouter(lazy(() => import('./pages/Documents/SoftwareRequirements')));
const ProcessingPolicy = withRouter(lazy(() => import('./pages/Documents/ProcessingPolicy')));
const Page404 = withRouter(lazy(() => import('./pages/Page404/Page404')));
const LegalDocuments = withRouter(lazy(() => import('./pages/Documents/LegalDocuments')));
const DocumentsFile = withRouter(lazy(() => import('./pages/DocumentsFile')));
const ServiceTermsPage = withRouter(lazy(() => import('./pages/Documents/ServiceTermsPage')));
const SafeMnjPage = withRouter(lazy(() => import('./pages/SafeMnjPage/SafeMnjPage')));
const HelpPage = withRouter(lazy(() => import('./pages/Help/HelpPage')));
const ProfileSettings = withRouter(lazy(() => import('./pages/ProfileSettings/ProfileSettings')));
const Verify = withRouter(lazy(() => import('./pages/Verify/Verify')));
const VideoPoll = withRouter(lazy(() => import('./pages/VideoPoll')));
const MyVideoPoll = withRouter(lazy(() => import('./pages/MyVideoPoll')));
const CreateVideoPoll = withRouter(lazy(() => import('./pages/CreateVideoPoll')));
const SentPolls = withRouter(lazy(() => import('./pages/VideoPollSent')));
const DashboardContacts = withRouter(lazy(() => import('./pages/ContactsListPage/ContactsListPage')));
const AdminPanelPage = withRouter(lazy(() => import('./pages/AdminPanelPage/AdminPanelPage')));
const UserListPage = withRouter(lazy(() => import('./pages/AdminPanelPage/Users/UserListPage/UserListPage')));
const AddNewUserPage = withRouter(lazy(() => import('./pages/AdminPanelPage/Users/AddNewUserPage/AddNewUserPage')));
const EditUserPage = withRouter(lazy(() => import('./pages/AdminPanelPage/Users/EditUserPage/EditUserPage')));
const PortfolioPage = withRouter(lazy(() => import('./pages/PortfolioPage/PortfolioPage')));
const TariffsList = withRouter(lazy(() => import('./pages/AdminPanelPage/Tariffs/TariffsList')));
const TariffCreate = withRouter(lazy(() => import('./pages/AdminPanelPage/Tariffs/TariffCreate')));
const TariffEdit = withRouter(lazy(() => import('./pages/AdminPanelPage/Tariffs/TariffEdit')));
const TariffCreateService = withRouter(lazy(() => import('./pages/AdminPanelPage/Tariffs/TariffCreateService')));
const LandingPageSectionSettings = withRouter(lazy(() => import('./pages/AdminPanelPage/LandingPageSectionSettings')));
const HowItWork = withRouter(lazy(() => import('./pages/AdminPanelPage/HowItWork')));
const AdminPanelVideoPollTemplates = withRouter(lazy(() => import('./pages/AdminPanelPage/VideoPollTemplates')));
const IndividualTariffs = withRouter(lazy(() => import('./pages/AdminPanelPage/IndividualTariffs')));
const IndividualTariffsAccessCreate = withRouter(lazy(() => import('./pages/AdminPanelPage/IndividualTariffs/IndividualTariffCreate')));
const IndividualTariffServiceCreate = withRouter(lazy(() => import('./pages/AdminPanelPage/IndividualTariffs/IndividualTariffCreateService')));
const IndividualTariffEdit = withRouter(lazy(() => import('./pages/AdminPanelPage/IndividualTariffs/IndividualTariffEdit')));
const TariffIssuance = withRouter(lazy(() => import('./pages/AdminPanelPage/TariffIssuance')));
const SendTariffIssuance = withRouter(lazy(() => import('./pages/AdminPanelPage/TariffIssuance/SendTariffIssuance')));
const SentTariffIssuance = withRouter(lazy(() => import('./pages/AdminPanelPage/TariffIssuance/SentTariffIssuance')));
const FeedbackPage = withRouter(lazy(() => import('./pages/FeedbackPage/FeedbackPage')));
const InstructionPage = withRouter(lazy(() => import('./pages/AdminPanelPage/Instructions/InstructionPage')));
const VideoMaterials = withRouter(lazy(() => import('./pages/AdminPanelPage/VideoMaterials')));
const Documents = withRouter(lazy(() => import('./pages/AdminPanelPage/Documents')));
const DocumentsEdit = withRouter(lazy(() => import('./pages/AdminPanelPage/Documents/DocumentsEdit')));
const AdminPanelAboutCompany = lazy(() => import('./pages/AdminPanelPage/AboutCompany'));
const AdminPanelNews = lazy(() => import('./pages/AdminPanelPage/News/AdminPanelNews'));
const AdminPanelNewsCreate = lazy(() => import('./pages/AdminPanelPage/News/AdminPanelNewsCreate'));
const AdminPanelContacts = lazy(() => import('./pages/AdminPanelPage/Contacts/AdminPanelContacts'));
const FbAuthPage = withRouter(lazy(() => import('./pages/SocialAuth/FbAuthPage')));
const VkAuthPage = withRouter(lazy(() => import('./pages/SocialAuth/VkAuthPage')));
const OkAuthPage = withRouter(lazy(() => import('./pages/SocialAuth/OkAuthPage')));
const MailRuAuthPage = withRouter(lazy(() => import('./pages/SocialAuth/MailRuAuthPage')));
const FbLinkAccountPage = withRouter(lazy(() => import('./pages/LinkAccount/FbLinkAccountPage')));
const VkLinkAccountPage = withRouter(lazy(() => import('./pages/LinkAccount/VkLinkAccountPage')));
const OkLinkAccountPage = withRouter(lazy(() => import('./pages/LinkAccount/OkLinkAccountPage')));
const MailRuLinkAccountPage = withRouter(lazy(() => import('./pages/LinkAccount/MailRuLinkAccountPage')));
const CardPage = withRouter(lazy(() => import('./pages/CartPage/CartPage')));
const CardPay = withRouter(lazy(() => import('./pages/Payment/CardPay')));
const HowItWorkPage = withRouter(lazy(() => import('./pages/HowItWorkPage')));
const RequisitesPay = withRouter(lazy(() => import('./pages/Payment/RequisitesPay')));
const Payments = withRouter(lazy(() => import('./pages/AdminPanelPage/Payments/Payments')));
const VideoPollList = withRouter(lazy(() => import('./pages/VideoPollList')));
const VideoPollFavorite = withRouter(lazy(() => import('./pages/VideoPollFavorite')));
const VideoPollTemplates = withRouter(lazy(() => import('./pages/VideoPollTemplates')));
const VideoPollRecord = withRouter(lazy(() => import('./pages/VideoPollRecord')));
const PassedVideoPoll = withRouter(lazy(() => import('./pages/PassedVideoPoll/PassedVideoPoll')));
const FounderPage = withRouter(lazy(() => import('./pages/FounderPage')));
const FounderPageDetails = withRouter(lazy(() => import('./pages/FounderPage/FounderPageDetails')));
const RecordRequest = withRouter(lazy(() => import('./pages/RecordRequest')));
const FeedPage = withRouter(lazy(() => import('./pages/FeedPage')));
const AdminPanelFeed = withRouter(lazy(() => import('./pages/AdminPanelPage/Feed')));
const AdminPanelSoftwareRequirements = withRouter(lazy(() => import('./pages/AdminPanelPage/SoftwareRequirements')));
const OnlinePollsCreate = withRouter(lazy(() => import('./pages/AdminPanelPage/OnlinePolls/OnlinePollsCreate')));
const OnlinePollsList = withRouter(lazy(() => import('./pages/AdminPanelPage/OnlinePolls/OnlinePollsList')));
const PassedOnlinePolls = withRouter(lazy(() => import('./pages/AdminPanelPage/OnlinePolls/PassedOnlinePolls')));
const OnlinePollsStats = withRouter(lazy(() => import('./pages/AdminPanelPage/OnlinePolls/OnlinePollsStats')));
const CompanyRequsites = withRouter(lazy(() => import('./pages/AdminPanelPage/CompanyRequsites')));
const ControlPanel = withRouter(lazy(() => import('./pages/ControlPanel')));
const ControlPanelVideo = withRouter(lazy(() => import('./pages/ControlPanel/ControlPanelVideo')));
const HtmlLayouts = withRouter(lazy(() => import('./pages/HtmlLayouts/HtmlLayoutEntry')));
const UserProfile = withRouter(lazy(() => import('./pages/UserProfile')));
const OnlinePoll = withRouter(lazy(() => import('./pages/OnlinePoll')));
const VideoSession = withRouter(lazy(() => import('./pages/VideoSession')));
const MyVideoRecords = withRouter(lazy(() => import('./pages/MyVideoRecords')));

function Routes() {
  return (
    <RootLayout>
      <Suspense fallback={<CommonPageWrap loader />}>
        <Switch>
          <Route exact path={ROUTES.ROOT.route} component={IndexPage} />
          <Route exact path={ROUTES.FEED.route} component={FeedPage} />
          <Route exact path={ROUTES.FEED_POST.route} component={FeedPage} />
          <Route exact path={ROUTES.LOGIN.route} component={notAuth(LoginPage)} />
          <Route exact path={ROUTES.REGISTRATION.route} component={notAuth(RegistrationPage)} />
          <Route
            exact
            path={oneOf([URL_MAP.CANDIDATE, URL_MAP.EMPLOYERS, URL_MAP.AGENCY, URL_MAP.SERVICE], URL_MAP.REGISTRATION)}
            component={notAuth(RegistrationPage)}
          />
          <Route exact path={ROUTES.VIDEO_SESSION.route} component={VideoSession} />
          <Route exact path={ROUTES.VIDEO_RESUME.route} component={VideoSession} />
          <Route exact path={ROUTES.MY_VIDEO_RESUME.route} component={MyVideoRecords} />
          <Route exact path={ROUTES.VIDEO_VACANCY.route} component={VideoSession} />
          <Route exact path={ROUTES.MY_VIDEO_VACANCY.route} component={MyVideoRecords} />
          <Route exact path={ROUTES.VIDEO_BUSINESS_CARD.route} component={VideoSession} />
          <Route exact path={ROUTES.MY_VIDEO_BUSINESS_CARD.route} component={MyVideoRecords} />
          <Route exact path={ROUTES.RESTORE_PASSWORD.route} component={notAuth(RestorePasswordPage)} />
          <Route exact path={ROUTES.ABOUT_COMPANY.route} component={AboutCompany} />
          <Route exact path={ROUTES.YOU_CANDIDATE.route} component={YouCandidatePage} />
          <Route exact path={ROUTES.YOU_EMPLOYER.route} component={YouEmployerPage} />
          <Route exact path={ROUTES.YOU_AGENCY.route} component={YouAgencyPage} />
          <Route exact path={ROUTES.CANDIDATE_SERVICE_DETAILS.route} component={RoleDetailsPage} />
          <Route exact path={ROUTES.EMPLOYER_SERVICE_DETAILS.route} component={RoleDetailsPage} />
          <Route exact path={ROUTES.AGENCY_SERVICE_DETAILS.route} component={RoleDetailsPage} />
          <Route exact path={ROUTES.NEWS.route} component={NewsListPage} />
          <Route exact path={ROUTES.NEWS_PAGE.route} component={NewsPage} />
          <Route exact path={ROUTES.PLANS_DETAILS.route} component={TariffPage} />
          <Route exact path={ROUTES.CONTACTS.route} component={ContactsPage} />
          <Route exact path={ROUTES.REQUISITES.route} component={RequisitesPage} />
          <Route exact path={ROUTES.TECHNICAL_SUPPORT.route} component={TechnicalSupport} />
          <Route exact path={ROUTES.SOFTWARE_REQUIREMENTS.route} component={SoftwareRequirements} />
          <Route exact path={ROUTES.DOCUMENTS_FILE.route} component={DocumentsFile} />
          <Route exact path={ROUTES.PROCESSING_POLICY.route} component={ProcessingPolicy} />
          <Route exact path={ROUTES.LEGAL_DOCUMENTS.route} component={LegalDocuments} />
          <Route exact path={ROUTES.SERVICE_TERMS.route} component={ServiceTermsPage} />
          <Route exact path={ROUTES.SAFE_MNJ.route} component={SafeMnjPage} />
          <Route exact path={ROUTES.ERROR_404.route} component={Page404} />
          <Route exact path={ROUTES.HELP.route} component={auth(HelpPage)} />
          <Route exact path={ROUTES.HOW_IT_WORK.route} component={HowItWorkPage} />
          <Route path={ROUTES.VERIFY.route} component={Verify} />
          <Route exact path={ROUTES.PROFILE_SETTINGS.route} component={auth(ProfileSettings)} />
          <Route exact path={ROUTES.MY_VIDEO_SESSION.route} component={auth(MyVideoRecords)} />
          <Route
            exact
            path={ROUTES.VIDEO_POLLS.route}
            component={requireAuth(VideoPoll, {
              roles: [EMPLOYER, RECRUITER, ASSISTANT, ADMINISTRATOR],
              authLevels: [USER, ADMIN, SUPER_ADMIN],
            })}
          />
          <Route
            exact
            path={ROUTES.MY_VIDEO_POLL.route}
            component={requireAuth(MyVideoPoll, {
              roles: [CANDIDATE],
              authLevels: [USER, ADMIN, SUPER_ADMIN],
            })}
          />
          <Route exact path={ROUTES.CREATE_POLL.route} component={auth(CreateVideoPoll)} />
          <Route exact path={ROUTES.SENT_POLLS.route} component={SentPolls} />
          <Route exact path={ROUTES.PASSED_VIDEO_POLL.route} component={PassedVideoPoll} />
          <Route exact path={ROUTES.VIDEO_POLL_START.route} component={VideoPollRecord} />
          <Route exact path={ROUTES.VIDEO_POLL_LIST.route} component={VideoPollList} />
          <Route exact path={ROUTES.VIDEO_POLL_ITEM.route} component={VideoPollList} />
          <Route exact path={ROUTES.VIDEO_POLL_FAVORITE.route} component={VideoPollFavorite} />
          <Route exact path={ROUTES.VIDEO_POLL_TEMPLATES.route} component={VideoPollTemplates} />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL.route}
            component={requireAuth(AdminPanelPage, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_USERS_LIST.route}
            component={requireAuth(UserListPage, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_CREATE_USER.route}
            component={requireAuth(AddNewUserPage, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_EDIT_USER.route}
            component={requireAuth(EditUserPage, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFFS.route}
            component={requireAuth(TariffsList, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_CREATE_TARIFF.route}
            component={requireAuth(TariffCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_EDIT.route}
            component={requireAuth(TariffEdit, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_CREATE_TARIFF_SERVICE.route}
            component={requireAuth(TariffCreateService, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_SERVICE_EDIT.route}
            component={requireAuth(TariffEdit, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INSTRUCTIONS.route}
            component={requireAuth(InstructionPage, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_PAYMENTS.route}
            component={requireAuth(Payments, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_LANDING_SECTION_SETTINGS.route}
            component={requireAuth(LandingPageSectionSettings, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_FEED.route}
            component={requireAuth(AdminPanelFeed, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          {/* <Route */}
          {/*  exact */}
          {/*  path={ROUTES.ADMIN_PANEL_ONLINE_POLLS.route} */}
          {/*  component={requireAuth(OnlinePolls, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})} */}
          {/* /> */}
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_ONLINE_POLLS_CREATE.route}
            component={requireAuth(OnlinePollsCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_ONLINE_POLLS_LIST.route}
            component={requireAuth(OnlinePollsList, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_ONLINE_POLLS.route}
            component={requireAuth(PassedOnlinePolls, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_ONLINE_POLLS_STATS.route}
            component={requireAuth(OnlinePollsStats, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_REQUISITES.route}
            component={requireAuth(CompanyRequsites, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_HOW_IT_WORK.route}
            component={requireAuth(HowItWork, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_VIDEO_POLL_TEMPLATES.route}
            component={requireAuth(AdminPanelVideoPollTemplates, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INDIVIDUAL_TARIFFS.route}
            component={requireAuth(IndividualTariffs, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INDIVIDUAL_TARIFF_CREATE.route}
            component={requireAuth(IndividualTariffsAccessCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INDIVIDUAL_TARIFF_EDIT.route}
            component={requireAuth(IndividualTariffEdit, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INDIVIDUAL_TARIFF_SERVICE_CREATE.route}
            component={requireAuth(IndividualTariffServiceCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_INDIVIDUAL_TARIFF_SERVICE_EDIT.route}
            component={requireAuth(IndividualTariffEdit, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_ISSUANCE.route}
            component={requireAuth(TariffIssuance, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_ISSUANCE_STANDARD_TARIFF.route}
            component={requireAuth(SendTariffIssuance, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_ISSUANCE_INDIVIDUAL_TARIFF.route}
            component={requireAuth(SendTariffIssuance, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_ISSUANCE_STANDARD_TARIFF_SENT.route}
            component={requireAuth(SentTariffIssuance, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_TARIFF_ISSUANCE_INDIVIDUAL_TARIFF_SENT.route}
            component={requireAuth(SentTariffIssuance, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_VIDEO_MATERIALS.route}
            component={requireAuth(VideoMaterials, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_DOCUMENTS.route}
            component={requireAuth(Documents, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_DOCUMENTS_EDIT.route}
            component={requireAuth(DocumentsEdit, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_ABOUT_COMPANY.route}
            component={requireAuth(AdminPanelAboutCompany, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_SOFTWARE_REQUIREMENTS.route}
            component={requireAuth(AdminPanelSoftwareRequirements, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_NEWS.route}
            component={requireAuth(AdminPanelNews, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_NEWS_CREATE.route}
            component={requireAuth(AdminPanelNewsCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_NEWS_EDIT.route}
            component={requireAuth(AdminPanelNewsCreate, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.ADMIN_PANEL_CONTACTS.route}
            component={requireAuth(AdminPanelContacts, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.CONTROL_PANEL.route}
            component={requireAuth(ControlPanel, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route
            exact
            path={ROUTES.CONTROL_PANEL_VIDEO.route}
            component={requireAuth(ControlPanelVideo, {roles: [], authLevels: [ADMIN, SUPER_ADMIN]})}
          />
          <Route exact path={ROUTES.FEEDBACK.route} component={FeedbackPage} />
          <Route exact path={ROUTES.AUTH_FB.route} component={FbAuthPage} />
          <Route exact path={ROUTES.AUTH_VK.route} component={VkAuthPage} />
          <Route exact path={ROUTES.AUTH_OK.route} component={OkAuthPage} />
          <Route exact path={ROUTES.AUTH_MAIL_RU.route} component={MailRuAuthPage} />
          <Route exact path={ROUTES.ACCOUNT_LINK_FB.route} component={FbLinkAccountPage} />
          <Route exact path={ROUTES.ACCOUNT_LINK_VK.route} component={VkLinkAccountPage} />
          <Route exact path={ROUTES.ACCOUNT_LINK_OK.route} component={OkLinkAccountPage} />
          <Route exact path={ROUTES.ACCOUNT_LINK_MAIL_RU.route} component={MailRuLinkAccountPage} />
          <Route exact path={ROUTES.CARD_PAYMENT.route} component={auth(CardPay)} />
          <Route exact path={ROUTES.REQUISITES_PAYMENT.route} component={auth(RequisitesPay)} />
          <Route exact path={ROUTES.CART.route} component={auth(CardPage)} />
          <Route exact path={ROUTES.FOUNDERS.route} component={FounderPage} />
          <Route exact path={ROUTES.FOUNDERS_DETAILS.route} component={FounderPageDetails} />
          <Route exact path={ROUTES.RECORD_REQUEST.route} component={RecordRequest} />
          {/* dashboard routes */}
          <Route path={ROUTES.USER_PROFILE.route} component={UserProfile} />
          <Route exact path={ROUTES.PROFILE_CONTACTS.route} component={DashboardContacts} />
          <Route exact path={ROUTES.PORTFOLIO.route} component={PortfolioPage} />
          <Route path={ROUTES.HTML_LAYOUTS.route} component={HtmlLayouts} />
          <Route exact path={ROUTES.ONLINE_POLL.route} component={OnlinePoll} />
          <Redirect from="*" to={ROUTES.ERROR_404.route} />
        </Switch>
      </Suspense>
    </RootLayout>
  );
}

Routes.propTypes = {
  history: PropTypes.shape({}).isRequired,
};

export default Routes;
